import React, { useState, useEffect, useRef, useCallback } from 'react';

import { buscarTabelaFiscal } from '../services/tabelasFiscais';

/* Escolha de código fiscal (NBS, CTN) a partir da tabela OFICIAL da NFS-e.

   Existe porque digitar esses códigos de cabeça é risco real: código errado
   passa pela Receita (ela não valida NBS) e vira autuação pro cliente. Aqui
   só dá pra escolher o que consta na tabela publicada no Anexo B, e o que
   está salvo aparece com a descrição ao lado — um código trocado fica
   visível na hora.

   A busca é no servidor: a tabela tem quase mil linhas e o escritório
   procura por palavra ("apoio administrativo"), não por número. */
function SeletorCodigoFiscal({ tabela, valor, onChange, className, placeholder }) {
  const [busca, setBusca] = useState('');
  const [itens, setItens] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [descricaoAtual, setDescricaoAtual] = useState('');
  const caixa = useRef(null);

  /* mostra a descrição do que já está salvo, sem carregar a lista toda */
  useEffect(() => {
    const codigo = String(valor || '').replace(/\D/g, '');
    if (!codigo) { setDescricaoAtual(''); return; }
    let vivo = true;
    buscarTabelaFiscal(tabela, { busca: codigo, limite: 1 })
      .then(r => {
        if (!vivo) return;
        const achado = (r.items || []).find(i => i.codigo === codigo);
        setDescricaoAtual(achado ? achado.descricao : '— não consta na tabela oficial');
      })
      .catch(() => vivo && setDescricaoAtual(''));
    return () => { vivo = false; };
  }, [valor, tabela]);

  const procurar = useCallback((termo) => {
    buscarTabelaFiscal(tabela, { busca: termo, limite: 40 })
      .then(r => setItens(r.items || []))
      .catch(() => setItens([]));
  }, [tabela]);

  useEffect(() => {
    if (!aberto) return;
    const t = setTimeout(() => procurar(busca), 200);
    return () => clearTimeout(t);
  }, [busca, aberto, procurar]);

  useEffect(() => {
    function fora(e) {
      if (caixa.current && !caixa.current.contains(e.target)) setAberto(false);
    }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  /* digitou o código inteiro e saiu do campo (ou deu Enter) sem clicar na
     lista: antes isso se perdia calado e o perfil gravava sem NBS. Agora,
     se o número digitado é um código da tabela, ele é o escolhido. */
  async function confirmarDigitado() {
    const d = busca.replace(/\D/g, '');
    if (!d || d === String(valor || '').replace(/\D/g, '')) return;
    let achado = itens.find(i => i.codigo === d);
    if (!achado) {
      try { achado = ((await buscarTabelaFiscal(tabela, { busca: d, limite: 5 })).items || []).find(i => i.codigo === d); }
      catch { achado = null; }
    }
    if (achado) onChange(achado.codigo);
  }

  const naoConsta = descricaoAtual.startsWith('—');

  return (
    <div ref={caixa} style={{ position: 'relative' }}>
      <input
        className={className}
        value={aberto ? busca : (valor || '')}
        placeholder={placeholder || 'Digite o código ou parte da descrição...'}
        onFocus={() => { setBusca(''); setAberto(true); }}
        onChange={e => { setBusca(e.target.value); setAberto(true); }}
        onBlur={() => { if (aberto) { confirmarDigitado(); setAberto(false); } }}
        onKeyDown={e => {
          if (e.key !== 'Enter' || !aberto) return;
          e.preventDefault();
          const d = busca.replace(/\D/g, '');
          const escolha = itens.find(i => i.codigo === d) || (itens.length === 1 ? itens[0] : null);
          if (escolha) { onChange(escolha.codigo); setAberto(false); }
        }}
      />
      {!aberto && valor && (
        <span style={{
          display: 'block', fontSize: 11, marginTop: 3, lineHeight: 1.3,
          color: naoConsta ? '#b45309' : '#6B8CAE',
        }}>
          {descricaoAtual}
        </span>
      )}

      {aberto && (
        <ul style={{
          position: 'absolute', zIndex: 60, top: '100%', left: 0, right: 0, marginTop: 2,
          maxHeight: 260, overflowY: 'auto', background: '#fff', listStyle: 'none',
          padding: 4, border: '1px solid #e4edf5', borderRadius: 9,
          boxShadow: '0 12px 30px rgba(13,31,60,.16)',
        }}>
          {itens.length === 0 && (
            <li style={{ padding: '8px 10px', fontSize: 12.5, color: '#8ba5c5' }}>
              {busca ? 'Nenhum código encontrado.' : 'Digite para procurar.'}
            </li>
          )}
          {itens.map(i => (
            <li key={i.codigo}>
              <button
                type="button"
                onMouseDown={e => { e.preventDefault(); onChange(i.codigo); setAberto(false); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', border: 0,
                  background: 'none', cursor: 'pointer', padding: '7px 9px',
                  borderRadius: 7, font: 'inherit', fontSize: 12.5, color: '#16202e',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f4f7fb'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              >
                <strong style={{ color: '#4A90D9', marginRight: 7 }}>{i.codigo}</strong>
                {i.descricao}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SeletorCodigoFiscal;
